<div class="stock-price-banding-container no-select">
  <div class="widget-div">
    @if (canPriceBandingBeViewed) {
      <app-shared-widget-container-component>
        <div title class="widget-div-heading">
          <app-display-icon
            [tooltip]="canPriceBandingBeEdited ? tooltips.editAccessGranted : tooltips.editAccessDenied"
            [icon]="canPriceBandingBeEdited ? icons.lockOpenOutline : icons.lockClosedOutline">
          </app-display-icon>
          <div class="heading-copy">
            <div>Price Banding</div>
            <p class="heading-subtitle">Set how edited prices snap to a friendlier ending. The first matching rule is used.</p>
          </div>
          <app-display-icon
            [tooltip]="tooltips.priceBanding"
            [icon]="icons.infoSlabCircleOutline"
            [colour]="iconColours.secondary">
          </app-display-icon>
        </div>

        <div block class="price-banding-block">
          @if (isSaving()) {
            <span class="source-note">Saving price banding rules…</span>
          } @else if (isLoading()) {
            <span class="source-note">Loading price bands…</span>
          }

          <section class="preview-card">
            <div class="preview-heading-row">
              <div class="preview-heading">Try a price</div>
              <p class="preview-help">Uses the same price banding as Stock Manager and NGP.</p>
            </div>
            <div class="preview-row">
              <div class="preview-input-block">
                <label for="price-banding-preview">Example price</label>
                <div class="preview-input-wrap">
                  <span class="preview-currency">R</span>
                  <input
                    id="price-banding-preview"
                    type="number"
                    min="0"
                    step="0.01"
                    [disabled]="!canPriceBandingBeEdited && !draftBands().length"
                    [value]="previewPrice()"
                    (input)="onPreviewPriceInput($event)"/>
                </div>
              </div>
              <div class="preview-arrow" aria-hidden="true">→</div>
              <div class="preview-result" [class.preview-result-changed]="previewResult() !== null && !previewIsUnchanged()">
                @if (previewResult() === null) {
                  <span class="preview-result-label">Enter a valid price</span>
                } @else if (previewIsUnchanged()) {
                  <span class="preview-result-label">Stays</span>
                  <strong class="preview-result-value">{{ formatMoney(previewResult()) }}</strong>
                  @if (previewMatchedRuleIndex() === null) {
                    <span class="preview-result-note">No matching rule</span>
                  } @else {
                    <span class="preview-result-note">Rule {{ previewMatchedRuleIndex() + 1 }} matches, no snap change</span>
                  }
                } @else {
                  <span class="preview-result-label">Becomes</span>
                  <strong class="preview-result-value">{{ formatMoney(previewResult()) }}</strong>
                  @if (previewMatchedRuleIndex() !== null) {
                    <span class="preview-result-note">Using Rule {{ previewMatchedRuleIndex() + 1 }}</span>
                  }
                }
              </div>
            </div>
          </section>

          <p class="rules-note">
            Rules are checked from top to bottom. The first rule whose range includes the price is used —
            later overlapping rules are ignored for that price.
          </p>

          <section class="rules-toolbar">
            <app-shared-button
              [colour]="'primary'"
              [disabled]="!canPriceBandingBeEdited"
              [fill]="true"
              (buttonClicked)="onAddRule()">
              Add rule
            </app-shared-button>
            <app-shared-button
              [colour]="'secondary'"
              [disabled]="!canPriceBandingBeEdited"
              (buttonClicked)="onRestoreGlobal()">
              Use Gallix Defaults
            </app-shared-button>
            <app-shared-button
              [colour]="'secondary'"
              [disabled]="!canPriceBandingBeEdited || draftBands().length === 0"
              (buttonClicked)="onClearAllRules()">
              Clear All Rules
            </app-shared-button>
          </section>

          @if (draftBands().length === 0) {
            <section class="empty-state">
              No price-banding rules yet. Add a rule, or use Gallix Defaults.
            </section>
          }

          @for (band of draftBands(); track $index; let idx = $index) {
            <article class="rule-card" [class.rule-card-open]="expandedRuleIndex() === idx">
              <header class="rule-header">
                <button type="button" class="rule-toggle" (click)="onToggleRule(idx)">
                  <app-display-icon
                    [icon]="expandedRuleIndex() === idx ? icons.chevronUp : icons.chevronDown">
                  </app-display-icon>
                  <div class="rule-title-block">
                    <div class="rule-title">Rule {{ idx + 1 }}</div>
                    @if (expandedRuleIndex() !== idx) {
                      <div class="rule-summary">{{ describeRuleRange(band) }}</div>
                      @if (overlapWarnings()[idx]) {
                        <div class="overlap-warning">{{ overlapWarnings()[idx] }}</div>
                      }
                    }
                  </div>
                </button>
                <button
                  type="button"
                  class="icon-button"
                  [disabled]="!canPriceBandingBeEdited"
                  aria-label="Remove rule"
                  (click)="onRemoveRule(idx)">
                  <app-display-icon
                    [colour]="canPriceBandingBeEdited ? iconColours.secondary : iconColours.disabled"
                    [icon]="icons.deleteOutline">
                  </app-display-icon>
                </button>
              </header>

              @if (expandedRuleIndex() === idx) {
                <div class="rule-body">
                  <div class="section-title">Applies to prices</div>
                  <div class="range-row">
                    <div class="form-control-div">
                      <label [for]="'rule-from-' + idx">From</label>
                      <input
                        [id]="'rule-from-' + idx"
                        type="number"
                        min="0"
                        step="0.01"
                        [disabled]="!canPriceBandingBeEdited"
                        [value]="band.range?.start?.v ?? 0"
                        (input)="onRangeValueChange(idx, 'start', $event)"/>
                      <label class="inclusive-label">
                        <input
                          type="checkbox"
                          [disabled]="!canPriceBandingBeEdited"
                          [checked]="band.range?.start?.inc !== false"
                          (change)="onRangeInclusiveChange(idx, 'start', $event)"/>
                        Include this amount
                      </label>
                    </div>
                    <div class="form-control-div">
                      <label [for]="'rule-to-' + idx">To</label>
                      <input
                        [id]="'rule-to-' + idx"
                        type="number"
                        min="0"
                        step="0.01"
                        [disabled]="!canPriceBandingBeEdited"
                        [value]="band.range?.end?.v ?? ''"
                        (input)="onRangeValueChange(idx, 'end', $event)"/>
                      <label class="inclusive-label">
                        <input
                          type="checkbox"
                          [disabled]="!canPriceBandingBeEdited"
                          [checked]="band.range?.end?.inc !== false"
                          (change)="onRangeInclusiveChange(idx, 'end', $event)"/>
                        Include this amount
                      </label>
                    </div>
                  </div>

                  <div class="section-title snap-title">
                    <span>Round the rands</span>
                    <app-switch-ui
                      [ariaLabel]="'Enable rand snaps for rule ' + (idx + 1)"
                      [checked]="!!band.randSnap"
                      [disabled]="!canPriceBandingBeEdited"
                      [size]="0.7"
                      (checkedChange)="onToggleRandSnaps(idx, $event)">
                    </app-switch-ui>
                  </div>
                  <p class="section-help">
                    Changes the last few digits of the whole-rand amount. Example: with 2 digits,
                    R1<strong>45</strong>.50 can become R1<strong>49</strong>.50.
                  </p>
                  @if (band.randSnap) {
                    <div class="digit-row">
                      <span>Digits to change</span>
                      <div class="digit-stepper">
                        <button type="button" class="icon-button" [disabled]="!canPriceBandingBeEdited" (click)="onRandDigitsChange(idx, -1)">
                          <app-display-icon [icon]="icons.chevronLeft"></app-display-icon>
                        </button>
                        <strong class="digit-value">{{ band.randSnap.digits }}</strong>
                        <button type="button" class="icon-button" [disabled]="!canPriceBandingBeEdited" (click)="onRandDigitsChange(idx, 1)">
                          <app-display-icon [icon]="icons.chevronRight"></app-display-icon>
                        </button>
                      </div>
                    </div>
                    @for (snap of band.randSnap.snaps; track $index; let snapIdx = $index) {
                      <div class="snap-row">
                        <div class="form-control-div">
                          <label>When last digits are from</label>
                          <input
                            type="number"
                            min="0"
                            [disabled]="!canPriceBandingBeEdited"
                            [value]="snap.range?.start?.v ?? 0"
                            (input)="onSnapRangeChange(idx, snapIdx, 'rand', 'start', $event)"/>
                        </div>
                        <div class="form-control-div">
                          <label>to</label>
                          <input
                            type="number"
                            min="0"
                            [disabled]="!canPriceBandingBeEdited"
                            [value]="snap.range?.end?.v ?? ''"
                            (input)="onSnapRangeChange(idx, snapIdx, 'rand', 'end', $event)"/>
                        </div>
                        <div class="form-control-div">
                          <label>change them to</label>
                          <input
                            type="number"
                            min="0"
                            [disabled]="!canPriceBandingBeEdited"
                            [value]="snap.v ?? 0"
                            (input)="onSnapValueChange(idx, snapIdx, 'rand', $event)"/>
                        </div>
                        <button
                          type="button"
                          class="icon-button"
                          [disabled]="!canPriceBandingBeEdited"
                          aria-label="Remove rand snap"
                          (click)="onRemoveSnap(idx, snapIdx, 'rand')">
                          <app-display-icon [icon]="icons.close"></app-display-icon>
                        </button>
                      </div>
                    }
                    <button
                      type="button"
                      class="add-snap-button"
                      [disabled]="!canPriceBandingBeEdited"
                      (click)="onAddSnap(idx, 'rand')">
                      <app-display-icon [border]="false" [colour]="iconColours.primary" [icon]="icons.plus"></app-display-icon>
                      Add rand snap
                    </button>
                  }

                  <div class="section-title snap-title">
                    <span>Round the cents</span>
                  </div>
                  <p class="section-help">
                    Changes only the cents. Example: R19.<strong>47</strong> can become R19.<strong>99</strong>.
                  </p>
                  @for (snap of band.centSnaps; track $index; let snapIdx = $index) {
                    <div class="snap-row">
                      <div class="form-control-div">
                        <label>When cents are from</label>
                        <input
                          type="number"
                          min="0"
                          max="99"
                          [disabled]="!canPriceBandingBeEdited"
                          [value]="snap.range?.start?.v ?? 0"
                          (input)="onSnapRangeChange(idx, snapIdx, 'cents', 'start', $event)"/>
                      </div>
                      <div class="form-control-div">
                        <label>to</label>
                        <input
                          type="number"
                          min="0"
                          max="99"
                          [disabled]="!canPriceBandingBeEdited"
                          [value]="snap.range?.end?.v ?? ''"
                          (input)="onSnapRangeChange(idx, snapIdx, 'cents', 'end', $event)"/>
                      </div>
                      <div class="form-control-div">
                        <label>change them to</label>
                        <input
                          type="number"
                          min="0"
                          max="99"
                          [disabled]="!canPriceBandingBeEdited"
                          [value]="snap.v ?? 0"
                          (input)="onSnapValueChange(idx, snapIdx, 'cents', $event)"/>
                      </div>
                      <button
                        type="button"
                        class="icon-button"
                        [disabled]="!canPriceBandingBeEdited"
                        aria-label="Remove cent snap"
                        (click)="onRemoveSnap(idx, snapIdx, 'cents')">
                        <app-display-icon [icon]="icons.close"></app-display-icon>
                      </button>
                    </div>
                  }
                  <button
                    type="button"
                    class="add-snap-button"
                    [disabled]="!canPriceBandingBeEdited"
                    (click)="onAddSnap(idx, 'cents')">
                    <app-display-icon [border]="false" [colour]="iconColours.primary" [icon]="icons.plus"></app-display-icon>
                    Add cent snap
                  </button>

                  @if (previewExamples(band).length) {
                    <div class="example-row">
                      @for (example of previewExamples(band); track example.from) {
                        <span class="example-chip">
                          {{ formatMoney(example.from) }} → {{ formatMoney(example.to) }}
                        </span>
                      }
                    </div>
                  }
                </div>
              }
            </article>
          }
        </div>

        <div actions class="widget-actions">
          @if (haveAnyEditsBeenMade) {
            <button
              type="button"
              class="action-button"
              [disabled]="!canPriceBandingBeEdited"
              (click)="onRevertForm()">
              <app-display-icon
                [colour]="canPriceBandingBeEdited ? iconColours.secondary : iconColours.disabled"
                [tooltip]="tooltips.undoChanges"
                [icon]="icons.undo">
              </app-display-icon>
            </button>
          }
          @if (canPriceBandingBeSubmitted) {
            <button
              type="button"
              class="action-button"
              [disabled]="isSubmitDisabled"
              (click)="onSubmitForm()">
              <app-display-icon
                [colour]="isSubmitDisabled ? iconColours.disabled : iconColours.secondary"
                [tooltip]="tooltips.saveChanges"
                [icon]="icons.contentSaveOutline">
              </app-display-icon>
            </button>
          }
        </div>
      </app-shared-widget-container-component>
    }
  </div>

  <div class="ups-container-action-bar widget-actions">
    @if (validationErrors().length > 0) {
      @for (field of validationErrors(); track field.field) {
        <button type="button" class="error-button">
          <app-display-icon
            [colour]="iconColours.red"
            [tooltip]="field.error"
            [icon]="icons.alertRhombusOutline">
          </app-display-icon>
        </button>
      }
    }
    <button
      [disabled]="!haveAnyEditsBeenMade"
      type="button"
      class="action-button"
      (click)="onRevertForm()">
      <app-display-icon
        [colour]="iconColours.secondary"
        [tooltip]="tooltips.undoAllChanges"
        [icon]="icons.undo">
      </app-display-icon>
      Undo Changes
    </button>
    <button
      [disabled]="isSubmitDisabled"
      type="button"
      class="action-button"
      (click)="onSubmitForm()">
      <app-display-icon
        [colour]="iconColours.secondary"
        [tooltip]="tooltips.saveAllChanges"
        [icon]="icons.contentSaveOutline">
      </app-display-icon>
      @if (isSaving()) {
        Saving…
      } @else {
        Save All
      }
    </button>
  </div>
</div>
